import React, { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../lib/api';
import { Badge } from '../lib/badge';
import Icon from '../components/icons';
import { EmptyState } from './ListView';
import { canManage } from '../lib/roles';
import RoleBanner from '../components/RoleBanner';

const TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'provisioned', label: 'Provisioned' },
  { key: 'rejected', label: 'Rejected' },
  { key: '', label: 'All' },
];

export default function Signups() {
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busyId, setBusyId] = useState(null);

  function load() {
    setLoading(true);
    setErr('');
    const path = tab ? `/signups?status=${tab}` : '/signups';
    apiGet(path)
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => setErr(String(e)))
      .finally(() => setLoading(false));
  }

  useEffect(load, [tab]);

  async function approve(id) {
    if (!canManage()) return;
    setBusyId(id);
    try {
      await apiPost(`/signups/${id}/approve`, {});
      load();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusyId(null);
    }
  }

  async function reject(id) {
    if (!canManage()) return;
    setBusyId(id);
    try {
      await apiPost(`/signups/${id}/reject`, {});
      load();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <main className="page">
      <div className="page__header">
        <span className="page__icon">
          <Icon name="customers" />
        </span>
        <div className="page__header-text">
          <h2>Plan Signups</h2>
          <p>Prospects from the public pricing page. Approve to provision a customer, workspace, and subscription.</p>
        </div>
      </div>

      <RoleBanner action="approve or reject signups" adminOnly />

      <div className="filter-chips" style={{ marginBottom: '0.9rem' }}>
        {TABS.map((t) => (
          <button
            key={t.key || 'all'}
            type="button"
            className={`filter-chip ${tab === t.key ? 'filter-chip--active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {err && <div className="status status--error">{err}</div>}

      {loading ? (
        <p className="cell-muted">Loading…</p>
      ) : rows.length === 0 ? (
        <EmptyState label="No signups in this view." />
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Company</th>
                <th>Plan</th>
                <th>Billing</th>
                <th>Source</th>
                <th>Status</th>
                <th>Submitted</th>
                {canManage() && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td>{r.email}</td>
                  <td>{r.company || <span className="cell-muted">—</span>}</td>
                  <td><Badge tone="info">{r.plan_key}</Badge></td>
                  <td>{r.billing_cycle}</td>
                  <td>
                    {r.stripe_checkout_session_id
                      ? <Badge tone="success">Stripe</Badge>
                      : <Badge tone="muted">Manual</Badge>}
                  </td>
                  <td><Badge value={r.status} /></td>
                  <td>{new Date(r.created_at).toLocaleString()}</td>
                  {canManage() && (
                    <td>
                      {r.status === 'pending' ? (
                        <div className="page__header-actions">
                          <button
                            type="button"
                            className="btn-primary btn-small"
                            disabled={busyId === r.id}
                            onClick={() => approve(r.id)}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="btn-danger btn-small"
                            disabled={busyId === r.id}
                            onClick={() => reject(r.id)}
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="cell-muted">
                          {r.status} by {r.decided_by || '—'}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
